import type { Ref } from 'vue'
import { useLocale } from '../../store/LocaleStore'
import { useReadItems } from '../useReadItems'
import type {
  Country,
  NewsCategory,
} from '../../server/models/news/news-article'
import type { GetNewsRes } from '../../server/api/v2/news/index.types'
import type { SearchParamsNewsCategory } from '../../utils/newsCategoryTab'

type NewsArticlesRequestsState = Partial<Record<NewsCategory, GetNewsRes>>

type NewsParams = {
  category: NewsCategory
  locale: Country | string
  cursor: string | undefined
  sortBy: string
  contentSource?: string
}

export const useNewsArticleHeadlines = (
  category?: Ref<SearchParamsNewsCategory>
) => {
  const newsArticles = useState<NewsArticlesRequestsState>(
    'content:news:articles',
    () => ({})
  )
  const siteSettingsStore = useSiteSettingsStore()
  const { data: siteSettings } = storeToRefs(siteSettingsStore)
  const sortBy = siteSettings.value.sortNewsBy ?? 'clusterSize'
  const { sortItemsByReadStatus } = useReadItems()

  const selectedCategory = computed(() => category?.value || 'News')

  const categoryHeadlines = computed(() => {
    const categoryHeadline = newsArticles.value?.[selectedCategory.value]
    return categoryHeadline ?? { count: 0, items: [], nextPageCursor: null }
  })
  const cursor = computed(() => categoryHeadlines.value.nextPageCursor)

  const headlines = computed(() => {
    const items = categoryHeadlines.value?.items ?? []
    return siteSettings.value.enableArticleReordering
      ? sortItemsByReadStatus(items)
      : items
  })

  const hasMore = computed(
    () => categoryHeadlines.value.nextPageCursor !== null
  )
  const count = computed(() => categoryHeadlines.value.count)
  const { locale } = useLocale()

  const getCategoryValue = () => {
    if (selectedCategory.value === 'News') {
      if (locale === 'EN') {
        return 'NC2'
      } else {
        return 'NC1'
      }
    }
    return selectedCategory.value
  }

  const { data, status, error, execute } = useAsyncData(
    `content:news:articles:${selectedCategory.value}:${cursor.value}`,
    async () => {
      const { client } = await useOpenFeature()
      const contentSource = client.getStringValue('contentSource', 'dynamodb')
      const params: NewsParams = {
        category: getCategoryValue(),
        cursor: cursor.value || undefined,
        locale,
        sortBy,
        contentSource: contentSource ?? 'dynamodb',
      }
      return await $fetch<GetNewsRes>('/api/v2/news', { params })
    },
    {
      watch: [selectedCategory],
    }
  )

  watch(data, (newHeadlines) => {
    if (!newHeadlines) return

    const previousValue = newsArticles.value[selectedCategory.value]
    newsArticles.value[selectedCategory.value] = {
      count: (previousValue?.count || 0) + newHeadlines.count,
      items: [...(previousValue?.items || []), ...newHeadlines.items],
      nextPageCursor: newHeadlines.nextPageCursor,
    }
  })

  return {
    headlines,
    status,
    error,
    hasMore,
    count,
    fetchNextPage: async () => {
      if (!hasMore.value) return
      await execute()
    },
  }
}
